import React, { useState, useEffect, useCallback } from 'react';
import { notificationsApi } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Bell, BellRinging, Check, Trash, Eye, Handshake,
  ClipboardText, Users, Lightning, CheckCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const TYPE_ICONS = {
  deal_update: Handshake,
  task_reminder: ClipboardText,
  contact: Users,
  workflow: Lightning,
  info: Bell,
};

const TYPE_COLORS = {
  deal_update: 'bg-blue-500',
  task_reminder: 'bg-amber-500',
  contact: 'bg-emerald-500',
  workflow: 'bg-purple-500',
  info: 'bg-zinc-500',
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await notificationsApi.getAll({
        unread_only: filter === 'unread',
        limit: 100
      });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleMarkRead = async (id) => {
    try {
      await notificationsApi.markRead(id);
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      toast.success('All notifications marked as read');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationsApi.delete(id);
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Notifications</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-muted rounded-sm p-0.5">
            <button
              className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${filter === 'all' ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground'}`}
              onClick={() => setFilter('all')}
              data-testid="filter-all"
            >
              All
            </button>
            <button
              className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${filter === 'unread' ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground'}`}
              onClick={() => setFilter('unread')}
              data-testid="filter-unread"
            >
              Unread {unreadCount > 0 && `(${unreadCount})`}
            </button>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
              <CheckCircle size={16} className="mr-1" />
              Mark all read
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 border border-dashed border-border rounded-sm">
          <Bell size={40} className="text-muted-foreground mb-3 opacity-40" />
          <p className="text-muted-foreground">
            {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Notifications for deals, tasks, and workflows appear here
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((notif) => {
            const Icon = TYPE_ICONS[notif.type] || Bell;
            const dotColor = TYPE_COLORS[notif.type] || 'bg-zinc-500';
            return (
              <div
                key={notif.id}
                className={`bg-card border border-border p-4 rounded-sm transition-all hover:-translate-y-[1px] ${
                  !notif.read ? 'border-l-4 border-l-primary' : 'opacity-70'
                }`}
                data-testid={`notification-${notif.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-9 w-9 rounded-sm ${dotColor} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={18} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h4 className={`text-sm ${!notif.read ? 'font-semibold' : 'font-medium'}`}>
                        {notif.title}
                      </h4>
                      {!notif.read && (
                        <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0"></div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{notif.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatDateTime(notif.created_at)}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {!notif.read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkRead(notif.id)}
                        title="Mark as read"
                        data-testid={`mark-read-${notif.id}`}
                      >
                        <Check size={14} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(notif.id)}
                      className="text-destructive hover:text-destructive"
                      title="Delete"
                      data-testid={`delete-notif-${notif.id}`}
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
